import React, {
    useEffect, useState
} from 'react';
import { CookieSetOptions } from 'universal-cookie';
import {
    v4
} from 'uuid';
import {
    ThemeProvider, Theme
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type {
    AppProps
} from 'next/app';
import {
    useRouter
} from 'next/router';
import {
    useCookies
} from 'react-cookie/cjs';
import {
    datadogLogs
} from '@datadog/browser-logs';
import {
    Container
} from '@mui/material';

import '../styles/globals.css';
import Meta from '../components/core/meta';
import SessionContext from '../contexts/session';
import * as gtag from '../lib/tracking/gtag';
import {
    hotjarUserAttribute
} from '../lib/hotjar';
import {
    dataDogUrlErrorWhitelist
} from '../utils/data-dog-whitelist';
import {
    getWebsiteDomain
} from '../networking';
import theme_sunnyside_dual_dashboard from '../theme_sunnyside_dual_dashboard';
import theme_sunnyside from '../theme_sunnyside';
import {
    ampSetOnce
} from '../lib/amplitude';
import ErrorBoundary from '../components_sunnyside/error_boundary';
import {
    brazeSDK_changeUser
} from '../lib/braze';
import spacetime from 'spacetime';
import { SnapEventName } from '../lib/tracking/snapchat';
import { MetaTrackingEventName, MetaTrackingTypeEnum } from '../lib/tracking/meta';
import { COOKIE_KEYS, QUERY_PARAMS } from '../lib/tracking/utils';
import { RedditClientSideEvent } from '../lib/tracking/reddit';
import { XEvent } from '../lib/tracking/x';
import MedTrackingScripts from '../sections/trackingScripts/med';
import MedTrackingScripts2 from '../sections/trackingScripts/med2';
import { usesLegacyMedPixel, usesNewMedPixel } from '../components/diagnostic/config/utils';

const MED_TRACKING_PATHS = [
    '/mf/diagnostic/[...slug]',
    '/mf/address-confirm',
    '/mf/intake/[...slug]',
    '/mf/payment/error',
    '/mf/payment/success',
    '/mf/appointment',
    '/mf/appointment/confirmed'
];

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme { }
}

interface CustomPageProps { // <--- your custom page props
    // your props
    metaData
}

// used to detect and take care of user load race-condition
export const NOT_LOADED_HINT = 'notloadedonfrontend';

function MyApp({
    Component, pageProps
}: AppProps<CustomPageProps>): JSX.Element {
    const [user, setUser] = useState({
        id: '',
        email: NOT_LOADED_HINT,
        phone: '',
        givenName: '',
        familyName: '',
        birthDate: '',
        phoneAuthorized: '',
        gender: '',
        createdAt: '',
        timeZone: '',
        __featureAccess__: [],
        __statesUntyped__: [],
        stripe: {
            currency: ''
        },
        flowType: null,
        diagnosticInFlight: true,
        diagnosticInFlightWithSub: false,
        formId: '',
        hashId: '',
        __challenges__: [],
        __states__: [],
        userCommunityInfo: {
            isCoach: false
        },
        userPaymentEntity: {},
        abtest_bucket: 0,
        create_source: 'unknown',
        __roles__: [],
        diagnostic: {
            focusAreas: '',
            formId: '',
            otherGoals: '',
            topGoal: '',
            targetTotalDrinks: 0,
            targetTotalDryDays: 0,
            totalDrinksBaseline: 0,
            totalDryDaysBaseline: 0,
            id: ''
        }
    });
    const [ui, setUI] = useState({
        hideNav: false,
    });
    const [initialized, setInitialized] = useState(false);
    const initialSession = {
        user,
        setUser,
        initialized,
        ui,
        setUI,
    };

    const router = useRouter();
    // CheckoutType, TrialDays, PremiumOnly
    const {
        ct, td, po
    } = router.query;
    const [cookies, setCookie] = useCookies();

    useEffect(() => {
        if (!router.isReady) return;
        const domain = getWebsiteDomain();

        /**
         * Add UI Session Debug ID
         */
        const sessionCookie = cookies['uiSessionDebugId'] || v4();
        setCookie('uiSessionDebugId', sessionCookie, {
            path: '/',
            domain
        });

        const impactClickId = router.query[QUERY_PARAMS.IMPACT_CLICK_ID];
        const snapClickId = router.query[QUERY_PARAMS.SNAP_CLICK_ID] || router.query[QUERY_PARAMS.SNAP_CLICK_ID_2];
        const facebookClickId = router.query[QUERY_PARAMS.FACEBOOK_CLICK_ID];
        const tiktokClickId = router.query[QUERY_PARAMS.TIKTOK_CLICK_ID];
        const redditClickId = router.query[QUERY_PARAMS.REDDIT_CLICK_ID];
        const xClickId = router.query[QUERY_PARAMS.X_CLICK_ID];

        const cookieParams: CookieSetOptions = {
            path: '/',
            expires: new Date(new Date().getTime() + (90 * 24 * 60 * 60 * 1000)), // 90 days
            sameSite: 'lax',
            domain
        };

        /** check if param exists in url and if it not set before
         * if not set, set the cookie
         */
        if (impactClickId && !cookies[COOKIE_KEYS.IMPACT_CLICK_ID]) {
            setCookie(COOKIE_KEYS.IMPACT_CLICK_ID, impactClickId, cookieParams);
        }
        if (snapClickId && !cookies[COOKIE_KEYS.SNAP_CLICK_ID]) {
            setCookie(COOKIE_KEYS.SNAP_CLICK_ID, snapClickId, cookieParams);
        }
        if (facebookClickId && !cookies[COOKIE_KEYS.FACEBOOK_CLICK_ID]) {
            const now = Date.now();
            const constructedFbc = `fb.1.${now}.${facebookClickId}`;
            setCookie(COOKIE_KEYS.FACEBOOK_CLICK_ID, constructedFbc, cookieParams);
        }
        if (tiktokClickId && !cookies[COOKIE_KEYS.TIKTOK_CLICK_ID]) {
            setCookie(COOKIE_KEYS.TIKTOK_CLICK_ID, tiktokClickId, cookieParams);
        }
        if (redditClickId && !cookies[COOKIE_KEYS.REDDIT_CLICK_ID]) {
            setCookie(COOKIE_KEYS.REDDIT_CLICK_ID, redditClickId, cookieParams);
        }
        if (xClickId && !cookies[COOKIE_KEYS.X_CLICK_ID]) {
            setCookie(COOKIE_KEYS.X_CLICK_ID, xClickId, cookieParams);
        }

        const homeUrl = window.location.href.substr(window.location.origin.length);
        gtag.gaClientPageView(homeUrl);

        const handleRouteChange = (url: string) => {
            gtag.gaClientPageView(url);
            if (typeof window['fbq'] === 'function') {
                window['fbq'](MetaTrackingTypeEnum.TRACK, MetaTrackingEventName.PAGE_VIEW);
            }
            if (typeof window['ttq'] === 'function') {
                window['ttq'].page();
            }
            if (typeof window['snaptr'] === 'function') {
                window['snaptr']('track', SnapEventName.PAGE_VIEW);
            }
            if (typeof window['rdt'] === 'function') {
                window['rdt']('track', RedditClientSideEvent.PAGE_VISIT);
            }
            if (typeof window['twq'] === 'function') {
                window['twq']('event', XEvent.PageView);
            }
            gtag.gaObject({
                event: 'optimize.route_change'
            });
        };

        router.events.on('routeChangeComplete', handleRouteChange);
        return () => {
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [router?.events, router?.isReady]);

    /**
     * Logs all routes users land on for better debugging
     * Logs all routes for amplitude page views
     */
    useEffect(() => {
        datadogLogs.logger.log(router.asPath, {
            userId: user.id,
            uiSessionDebugId: cookies['uiSessionDebugId']
        });
        if (window !== undefined) {
            if (cookies['amplitude_once_ever'] === 'true') {
                return;
            }

            ampSetOnce('first_page_visited', window.location.href);

            // expires in 1 year, so first_page_visited isn't called again for a year for same browser
            const expiresAt = new Date(new Date().getTime() + (365 * 24 * 60 * 60 * 1000));
            setCookie('amplitude_once_ever', 'true', {
                path: '/',
                expires: expiresAt,
                domain: getWebsiteDomain()
            });
        }
    }, [router.asPath]);

    useEffect(() => {
        // Remove the server-side injected CSS.
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles) {
            jssStyles.parentElement.removeChild(jssStyles);
        }

        datadogLogs.init({
            clientToken: process.env.NEXT_PUBLIC_DATADOG_TOKEN,
            site: 'datadoghq.com',
            forwardErrorsToLogs: true,
            sampleRate: 100,
            env: process.env.NEXT_PUBLIC_NENV,
            service: process.env.NEXT_PUBLIC_DATADOG_SOURCE,
            beforeSend: (log) => {
                if (log.http && log.status === 'error') {
                    const {
                        hostname
                    } = new URL(log.http.url);
                    if (!dataDogUrlErrorWhitelist.some((url) => hostname.includes(url))) {
                        return false;
                    }
                }
            }
        });

        if (
            window !== undefined
            && (window.location.host.includes('app.') || window.location.host.includes('app2.'))
            || window.location.host === 'localhost') {
            import('../logic/auth').then((auth) => {
                auth.refreshUserAndStoreIn(initialSession).then(userData => {
                    // TODO - better handling of casting issue
                    // Type error: Type 'unknown' is not assignable to type 'string'.
                    if (!userData.message) {
                        setUser(userData);
                    }
                    setInitialized(true);
                }).catch(() => {
                    // Initialization must still complete: routes such as /l/[...slug]
                    // gate their whole flow on session.initialized and would otherwise
                    // hang on Loading forever. Fall through as the logged-out user.
                    setInitialized(true);
                });
            });
        }
    }, []);

    useEffect(()=>{
        if(!router.isReady) return;

        /**
        * Store no CC checkout logic
        */
        if (ct) {
            setCookie('ct', ct, {
                path: '/',
                expires: spacetime().add(1, 'day').toNativeDate(),
                domain: getWebsiteDomain()
            });
        }

        /**
         * Store trial days logic
         */
        if (td) {
            setCookie('td', td, {
                path: '/',
                expires: spacetime().add(1, 'day').toNativeDate(),
                domain: getWebsiteDomain()
            });
        }
        /**
         * Store Premium Only Logic
         */
        if (po) {
            setCookie('po', po, {
                path: '/',
                expires: spacetime().add(1, 'day').toNativeDate(),
                domain: getWebsiteDomain()
            });
        }
    }, [router.isReady]);

    /**
     * Hotjar set user for session
     */
    useEffect(() => {
        if (user.id) {
            hotjarUserAttribute(user.id, {
                ui_session_debug_id: cookies['uiSessionDebugId']
            });
            brazeSDK_changeUser(user.id);
        }
    }, [user]);

    /**
     * Different theme is temp for dashboard until full rebuild
     */
    const tempPaths = ['dashboard', 'plan', 'premium-coaching', 'challenge'];
    const theme = tempPaths.includes(router.pathname.split('/')[1]) ? theme_sunnyside_dual_dashboard : theme_sunnyside;

    return (
        <React.Fragment>
            <Meta metaData={pageProps.metaData} />
            <ThemeProvider theme={theme}>
                {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                <CssBaseline />
                <Container
                    disableGutters
                    maxWidth={false}
                >
                    <SessionContext.Provider value={initialSession}>
                        <ErrorBoundary>
                            {MED_TRACKING_PATHS.includes(router.pathname) && (
                                router?.query?.slug?.[0]
                                || router?.query?.formId
                            ) && (
                                <>
                                    {(
                                        usesLegacyMedPixel(router?.query?.slug?.[0])
                                        || usesLegacyMedPixel(router?.query?.formId as string)
                                    ) && (
                                        <MedTrackingScripts />
                                    )}
                                    {(
                                        usesNewMedPixel(router?.query?.slug?.[0])
                                        || usesNewMedPixel(router?.query?.formId as string)
                                    ) && (
                                        <MedTrackingScripts2 />
                                    )}
                                </>
                            )}
                            <Component {...pageProps} />
                        </ErrorBoundary>
                    </SessionContext.Provider>
                </Container>
            </ThemeProvider>
        </React.Fragment>
    );
}

export default MyApp;
